<template>
  <div class="page-container">
    <!-- 页面头部导航 -->
    <header class="header">
      <div class="header-content">
        <h1 class="header-title">游戏数据管理面板</h1>
        <div class="header-actions">
          <el-button type="primary" size="small">系统设置</el-button>
          <el-dropdown>
            <span class="user-dropdown">管理员 <el-icon><ArrowDown /></el-icon></span>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item>个人中心</el-dropdown-item>
                <el-dropdown-item>退出登录</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </div>
    </header>

    <!-- 主要内容区 -->
    <main class="main-content">
      <!-- 面包屑 -->
      <el-breadcrumb class="breadcrumb" separator="/">
        <el-breadcrumb-item><el-icon><House /></el-icon> 首页</el-breadcrumb-item>
        <el-breadcrumb-item>游戏数据</el-breadcrumb-item>
        <el-breadcrumb-item>数据面板</el-breadcrumb-item>
      </el-breadcrumb>

      <!-- 全局筛选容器 -->
      <div class="global-filter-container">
        <!-- 服务器选择组 -->
        <div class="filter-group">
          <label class="filter-label">选择游戏服务器：</label>
          <el-select
            v-model="activeServerId"
            @change="handleServerChange"
            placeholder="请选择服务器"
            class="filter-select"
            :loading="serverLoading"
            :disabled="!serverList.length"
          >
            <el-option
              v-for="server in serverList"
              :key="server.value"
              :label="server.label"
              :value="server.value"
            ></el-option>
          </el-select>
        </div>
        
        <!-- 时间选择组：改为日期范围选择器 -->
        <div class="filter-group">
          <label class="filter-label">选择查询时间段：</label>
          <el-date-picker
            v-model="selectedDateRange"
            @change="handleDateChange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            class="filter-select"
            format="YYYY-MM-DD"
            value-format="YYYY-MM-DD"
            :disabled-date="disabledFutureDate"
          ></el-date-picker>
        </div>
        
        <!-- 快捷时间按钮组 -->
        <div class="filter-group quick-date-group">
          <label class="filter-label">快捷选择：</label>
          <el-button-group>
            <el-button size="small" @click="selectToday">今天</el-button>
            <el-button size="small" @click="selectYesterday">昨天</el-button>
            <el-button size="small" @click="clearDate">清空</el-button>
          </el-button-group>
        </div>
        
        <!-- 调试信息 -->
        <div class="filter-debug">
          当前选中ID: {{ activeServerId }} | 选项数: {{ serverList.length }} | 
          选中时间段: {{ dateRangeText || '未选择' }} | 
          拼接参数: {{ selectedDate || '未选择' }}
        </div>
      </div>

      <!-- 日期切换区域 -->
      <div class="date-switch-container">
        <el-button @click="prevDate" :disabled="currentIndex <= 0">上一天</el-button>
        <span class="current-date">当前查看日期：{{ currentDate }}</span>
        <el-button @click="nextDate" :disabled="currentIndex >= dateList.length - 1">下一天</el-button>
      </div>

      <!-- 数据统计卡片 -->
      <div class="stats-card-container">
        <el-card class="stats-card">
          <template #header><span>总注册用户</span></template>
          <div class="stats-value">{{ totalUsers }}</div>
          <div class="stats-desc">累计</div>
        </el-card>

        <el-card class="stats-card">
          <template #header><span>今日登录</span></template>
          <div class="stats-value">{{ todayLogin }}</div>
          <div class="stats-desc">实时统计</div>
        </el-card>

        <el-card class="stats-card">
          <template #header><span>今日充值人数</span></template>
          <div class="stats-value">{{ todayRechargeUser }}</div>
          <div class="stats-desc">实时统计</div>
        </el-card>

        <el-card class="stats-card">
          <template #header><span>今日充值总额</span></template>
          <div class="stats-value">{{ chargeTotal }} 元</div>
          <div class="stats-desc">实时统计</div>
        </el-card>
      </div>

      <!-- 核心：切换标签 + 对应内容 -->
      <div class="tab-container">
        <el-tabs v-model="activeTab" class="tab-header">
          <el-tab-pane label="角色物品" name="roleItem"></el-tab-pane>
          <el-tab-pane label="系统数据" name="systemData"></el-tab-pane>
          <el-tab-pane label="用户截图查询" name="screenshot"></el-tab-pane>
          <el-tab-pane label="掉落物品数据" name="todayDrop"></el-tab-pane>
          <el-tab-pane label="全服金子铜钱数据" name="goldMoney"></el-tab-pane>
          <el-tab-pane label="改名角色转移查询" name="renameTransfer"></el-tab-pane>
          <el-tab-pane label="指定天数用户信息" name="userDaysStats"></el-tab-pane>
        </el-tabs>

        <div class="tab-content">
          <!-- 角色物品标签内容 -->
          <div v-if="activeTab === 'roleItem'">
            <div class="func-card">
              <div class="func-card-header">
                <h2 class="func-card-title">人物背包数据</h2>
              </div>
              <!-- 角色物品子标签 -->
              <el-tabs v-model="activeRoleSubTab" class="sub-tab-header">
                <el-tab-pane label="背包信息" name="backpackInfo"></el-tab-pane>
                <el-tab-pane label="物品到角色" name="itemToRole"></el-tab-pane>
                <el-tab-pane label="人物物品来源查询" name="roleItemSource"></el-tab-pane>
              </el-tabs>
              <!-- 子标签内容：给所有子组件传递共享数据 -->
              <div class="sub-tab-content">
                <div v-if="activeRoleSubTab === 'backpackInfo'">
                  <BackpackInfo 
                    :server-id="activeServerId" 
                    :selected-date="selectedDate"
                    :loading="dataLoading"
                  />
                </div>
                <div v-if="activeRoleSubTab === 'itemToRole'">
                  <ItemToRole 
                    :server-id="activeServerId" 
                    :selected-date="selectedDate"
                    :loading="dataLoading"
                  />
                </div>
                <div v-if="activeRoleSubTab === 'roleItemSource'">
                  <RoleItemSource 
                    :server-id="activeServerId" 
                    :selected-date="selectedDate"
                    :loading="dataLoading"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- 系统数据标签内容 -->
          <div v-if="activeTab === 'systemData'">
            <div class="func-card">
              <div class="func-card-header">
                <h2 class="func-card-title">游戏数据总览</h2>
              </div>
              <GameStatsPanel 
                :server-id="activeServerId" 
                :selected-date="selectedDate"
                :loading="dataLoading"
              />
            </div>
          </div>
          <!-- 用户截图查询 -->
          <div v-if="activeTab === 'screenshot'">
            <div class="func-card">
              <div class="func-card-header">
                <h2 class="func-card-title">用户截图查询</h2>
              </div>
              <UserScreenshotQuery />
            </div>
          </div>
          <div v-if="activeTab === 'todayDrop'">
          <div class="func-card">
            <div class="func-card-header">
              <h2 class="func-card-title">今日掉落物品明细</h2>
            </div>
            <TodayDropItems
              :server-id="activeServerId"
              :selected-date="selectedDate"
              :loading="dataLoading"
            />
          </div>
        </div>

          <div v-if="activeTab === 'goldMoney'">
              <div class="func-card">
                <AllServerGoldMoney
                  :server-id="activeServerId"
                  :selected-date="selectedDate"
                  :loading="dataLoading"
                />
              </div>
            </div>
            
            <div v-if="activeTab === 'renameTransfer'">
              <div class="func-card">
                <div class="func-card-header">
                  <h2 class="func-card-title">改名 & 角色转移日志查询</h2>
                </div>
                <UserRenameTransfer
                  :server-id="activeServerId"
                  :loading="dataLoading"
                />
              </div>
            </div>

            <div v-if="activeTab === 'userDaysStats'">
              <div class="func-card">
                <div class="func-card-header">
                  <h2 class="func-card-title">指定天数用户信息查询</h2>
                </div>
                <!-- 新增子组件，传递服务器id和loading -->
                <UserDaysStats
                  :server-id="activeServerId"
                  :server-name="currentServerName"
                  :selected-date="selectedDate"
                  :loading="dataLoading"
                />
              </div>
            </div>


         
        </div>
      </div>
    </main>

    <!-- 页脚 -->
    <footer class="footer">
      © 2026 游戏数据管理系统 - 所有数据仅供内部使用
    </footer>
  </div>
</template>

<script setup>
import { LOG_API } from '@/utils/apiBase';
import { ref, watch, onMounted, computed } from 'vue';
import axios from 'axios';
import { ElMessage } from 'element-plus';
import BackpackInfo from './BackpackInfo.vue';
import GameStatsPanel from './GameStatsPanel.vue';
import ItemToRole from './ItemToRole.vue';
import RoleItemSource from './RoleItemSource.vue';
import { House, ArrowDown } from '@element-plus/icons-vue';
import UserScreenshotQuery from './UserScreenshotQuery.vue';
import AllServerGoldMoney from './AllServerGoldMoney.vue'; // 加这行
import TodayDropItems from './TodayDropItems.vue'; // 加这行
import UserRenameTransfer from './UserRenameTransfer.vue'
import UserDaysStats from './UserDaysStats.vue'



// 响应式数据
const serverList = ref([]);         // 服务器列表
const serverLoading = ref(false);   // 服务器加载状态
const dataLoading = ref(false);     // 数据加载状态（所有子组件共享）
const activeServerId = ref('');     // 当前选中服务器ID
const chargeTotal = ref(0);         // 今日充值总额
const totalUsers = ref(0);          // 总用户
const todayLogin = ref(0);          // 今日登录
const todayRechargeUser = ref(0);   // 充值人数
const activeTab = ref('roleItem');  // 主标签切换
const activeRoleSubTab = ref('backpackInfo'); // 角色物品子标签
const selectedDateRange = ref([]);  // 日期范围数组 [开始日期, 结束日期]

// 日期切换相关
const dateList = ref([]);           // 所有日期数据列表
const currentIndex = ref(0);        // 当前查看的日期索引
const currentDate = computed(() => {
  return dateList.value[currentIndex.value]?.date || '无数据';
});

// 计算属性：拼接后端需要的日期参数 格式：开始日期|结束日期
const selectedDate = computed(() => {
  if (!selectedDateRange.value || selectedDateRange.value.length !== 2) {
    return '';
  }
  return selectedDateRange.value.join('|');
});

// 计算属性：页面显示用的日期文本
const dateRangeText = computed(() => {
  if (!selectedDateRange.value || selectedDateRange.value.length !== 2) {
    return '';
  }
  return `${selectedDateRange.value[0]} 至 ${selectedDateRange.value[1]}`;
});

// 方法：获取选中服务器的完整信息
const getSelectedServerInfo = () => {
  const selectedServer = serverList.value.find(server => server.value === activeServerId.value);
  return selectedServer || { label: '未选中', value: '' };
};

// 格式化日期工具函数
const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// 获取今天的日期
const getToday = () => {
  return formatDate(new Date());
};

// 获取昨天的日期
const getYesterday = () => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return formatDate(yesterday);
};

// 禁用未来日期（只能选今天及以前）
const disabledFutureDate = (time) => {
  return time.getTime() > Date.now();
};

// 获取当前选中服务器名称
const currentServerName = computed(() => {
  const server = serverList.value.find(item => item.value === activeServerId.value)
  return server?.label || ''
})

// 快捷选择今天
const selectToday = () => {
  const today = getToday();
  selectedDateRange.value = [today, today];
  handleDateChange();
};

// 快捷选择昨天
const selectYesterday = () => {
  const yesterday = getYesterday();
  selectedDateRange.value = [yesterday, yesterday];
  handleDateChange();
};

// 清空日期选择
const clearDate = () => {
  selectedDateRange.value = [];
  handleDateChange();
};

// 方法：输出服务器数据到控制台
const logServerData = (trigger = '默认选中') => {
  const selectedServer = getSelectedServerInfo();
  console.log(`===== ${trigger} 服务器数据 =====`);
  console.log('所有服务器列表:', serverList.value);
  console.log('选中的服务器ID:', activeServerId.value);
  console.log('选中的服务器完整信息:', selectedServer);
  console.log('选中的日期范围:', selectedDateRange.value);
  console.log('拼接后参数:', selectedDate.value);
  console.log('==========================');
};

// 核心方法：获取服务器列表
const getServerList = async () => {
  serverLoading.value = true;
  try {
    const res = await axios.get(`${LOG_API}/api/Recharge/GetGameServers`);
    
    if (res.data?.code === 200 && Array.isArray(res.data.data)) {
      serverList.value = res.data.data.map(item => ({
        label: item.ServerName || item.serverName || '未知服务器',
        value: item.ServerId || item.serverId || ''
      }));
      
      if (serverList.value.length > 0) {
        activeServerId.value = serverList.value[0].value;
        logServerData('默认选中');
        updateServerStats();
      } else {
        ElMessage.warning('暂无可用服务器数据');
        logServerData('无可用服务器');
      }
    } else {
      serverList.value = [];
      ElMessage.error(`获取服务器失败：${res.data?.msg || '接口返回异常'}`);
      logServerData('获取服务器失败');
    }
  } catch (err) {
    console.error('获取服务器列表失败:', err);
    serverList.value = [];
    if (err.response) {
      ElMessage.error(`获取服务器失败：${err.response.data?.msg || `状态码${err.response.status}`}`);
    } else if (err.request) {
      ElMessage.error('获取服务器失败：网络异常，请检查链接地址是否正确');
    } else {
      ElMessage.error('获取服务器失败：请求配置错误');
    }
    logServerData('请求异常');
  } finally {
    serverLoading.value = false;
  }
};

// 服务器切换处理
const handleServerChange = () => {
  updateServerStats();
  logServerData('手动切换服务器');
};

// 日期选择变化处理
const handleDateChange = () => {
  updateServerStats();
  logServerData('切换日期');
  
  if (selectedDate.value) {
    ElMessage.info(`已选择查询时间段：${dateRangeText.value}`);
  } else {
    ElMessage.info('已清空日期选择，将查询全部数据');
  }
};

// 日期切换方法
const prevDate = () => {
  if (currentIndex.value > 0) {
    currentIndex.value--;
    updateStatsByIndex();
  }
};

const nextDate = () => {
  if (currentIndex.value < dateList.value.length - 1) {
    currentIndex.value++;
    updateStatsByIndex();
  }
};

// 根据当前索引更新统计数据
const updateStatsByIndex = () => {
  const data = dateList.value[currentIndex.value];
  if (data) {
    totalUsers.value = data.totalUsers || 0;
    todayLogin.value = data.todayLogin || 0;
    todayRechargeUser.value = data.todayRechargeUser || 0;
    chargeTotal.value = Number(data.todayRechargeMoney || 0).toFixed(2);
  }
};

// ===================== 核心修改：接入你的 Stats 接口 =====================
const updateServerStats = async () => {
  if (!activeServerId.value) return;
  
  dataLoading.value = true;
  try {
    const res = await axios.get(`${LOG_API}/api/UserMessage/start`, {
      params: { serverName: activeServerId.value }
    });
    console.log('接口：start ',activeServerId.value);
    console.log('接口返回：', res.data);
    
    if (res.data?.code === 0 && res.data.data?.length > 0) {
      // 保存所有日期数据
      dateList.value = res.data.data;
      // 默认显示最新的一天（最后一条）
      currentIndex.value = dateList.value.length - 1;
      // 更新数据
      updateStatsByIndex();
    } else {
      dateList.value = [];
      currentIndex.value = 0;
      totalUsers.value = 0;
      todayLogin.value = 0;
      todayRechargeUser.value = 0;
      chargeTotal.value = 0;
    }
  } catch (error) {
    console.error('获取统计数据失败:', error);
    dateList.value = [];
    currentIndex.value = 0;
    totalUsers.value = 0;
    todayLogin.value = 0;
    todayRechargeUser.value = 0;
    chargeTotal.value = 0;
  } finally {
    dataLoading.value = false;
  }
};
// ======================================================================

// 监听服务器和日期变化自动更新数据
watch([activeServerId, selectedDate], updateServerStats, { immediate: false });

// 页面初始化
onMounted(async () => {
  await getServerList();
});
</script>

<style scoped>
/* 原有样式保持不变 */
/* 全局容器 */
.page-container {
  min-height: 100vh;
  background-color: #0f172a;
  color: #f1f5f9;
  font-family: "Microsoft YaHei", sans-serif;
}

/* 页面头部导航 */
.header {
  background-color: #1e293b;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  padding: 0 24px;
}
.header-content {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 60px;
}
.header-title {
  font-size: 20px;
  font-weight: bold;
  color: #60a5fa;
  margin: 0;
}
.header-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}
.user-dropdown {
  display: flex;
  align-items: center;
  color: #cbd5e1;
  cursor: pointer;
}

/* 主要内容区 */
.main-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
}

/* 面包屑 */
.breadcrumb {
  margin-bottom: 16px;
}
:deep(.el-breadcrumb__item) {
  color: #94a3b8 !important;
}
:deep(.el-breadcrumb__item:last-child) {
  color: #e2e8f0 !important;
}

/* 全局筛选容器样式 */
.global-filter-container {
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  padding: 12px 16px;
  background-color: #1e293b;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 20px;
}

/* 筛选组：标签+下拉框的组合容器 */
.filter-group {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 280px;
}

/* 快捷日期选择组特殊样式 */
.quick-date-group {
  min-width: 220px;
}

/* 筛选标签样式 */
.filter-label {
  font-size: 14px;
  color: #e0e0e0;
  white-space: nowrap;
}

/* 筛选下拉框/日期选择器样式 */
.filter-select {
  flex: 1;
  min-width: 180px;
  --el-select-bg-color: #2d2d44;
  --el-select-border-color: #3d3d5c;
  --el-select-text-color: #fff;
  --el-select-hover-border-color: #60a5fa;
  
  --el-date-editor-bg-color: #2d2d44;
  --el-date-editor-border-color: #3d3d5c;
  --el-date-editor-text-color: #fff;
  --el-date-editor-hover-border-color: #60a5fa;
}

/* 调试信息样式 */
.filter-debug {
  font-size: 12px;
  color: #999;
  margin-left: auto;
  white-space: nowrap;
}

/* 日期切换区域 */
.date-switch-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  margin-bottom: 24px;
  padding: 12px;
  background-color: #1e293b;
  border-radius: 8px;
}
.current-date {
  font-size: 16px;
  color: #fff;
  font-weight: 500;
}

/* 数据统计卡片 */
.stats-card-container {
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}
.stats-card {
  flex: 1;
  min-width: 280px;
  background-color: #1e293b !important;
  border: none !important;
  border-radius: 8px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}
:deep(.el-card__header) {
  background-color: transparent !important;
  border-bottom: none !important;
  padding: 12px 0 !important;
  color: #94a3b8;
}
.stats-value {
  font-size: 28px;
  font-weight: bold;
  color: #ffffff;
  margin: 8px 0 4px 0;
}
.stats-desc {
  font-size: 12px;
  color: #94a3b8;
}

/* 切换标签容器 */
.tab-container {
  background-color: #1e293b;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}
/* 主标签头部 */
.tab-header {
  border-bottom: 1px solid #334155;
}
:deep(.el-tabs__nav) {
  padding: 12px 16px;
}
:deep(.el-tabs__item) {
  color: #94a3b8;
  font-size: 14px;
  padding: 0 16px;
}
:deep(.el-tabs__item.is-active) {
  color: #60a5fa;
}
:deep(.el-tabs__active-bar) {
  background-color: #60a5fa;
}

/* 角色物品子标签样式 */
.sub-tab-header {
  margin: 0 16px 16px;
  border-bottom: 1px solid #334155;
}
:deep(.sub-tab-header .el-tabs__nav) {
  padding: 8px 0;
}
:deep(.sub-tab-header .el-tabs__item) {
  color: #94a3b8;
  font-size: 14px;
  padding: 0 12px;
}
:deep(.sub-tab-header .el-tabs__item.is-active) {
  color: #60a5fa;
}
:deep(.sub-tab-header .el-tabs__active-bar) {
  background-color: #60a5fa;
  height: 2px;
}
.sub-tab-content {
  padding: 0 16px 16px;
}

/* 标签内容 */
.tab-content {
  padding: 16px;
}
.func-card {
  background-color: #1e293b;
  border-radius: 8px;
  padding: 1px;
}
.func-card-header {
  padding: 12px 16px;
  border-bottom: 1px solid #334155;
  margin-bottom: 16px;
}
.func-card-title {
  font-size: 18px;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
}

/* 页脚 */
.footer {
  background-color: #1e293b;
  padding: 16px 0;
  text-align: center;
  color: #94a3b8;
  font-size: 12px;
  margin-top: 24px;
}

/* 下拉框加载状态 */
:deep(.el-select .el-input__wrapper) {
  --el-input-bg-color: #2d2d44;
  --el-input-border-color: #3d3d5c;
}
:deep(.el-select-loading) {
  color: #60a5fa;
}

/* 日期选择器弹窗样式适配 */
:deep(.el-popper) {
  background-color: #1e293b !important;
  border: 1px solid #3d3d5c !important;
}
:deep(.el-date-table) {
  color: #e2e8f0 !important;
}
:deep(.el-date-table td) {
  color: #e2e8f0 !important;
}
:deep(.el-date-table .el-date-table__row td.available:hover) {
  background-color: #334155 !important;
}
:deep(.el-date-table .current) {
  color: #fff !important;
  background-color: #60a5fa !important;
}
:deep(.el-date-picker__header-label) {
  color: #e2e8f0 !important;
}
:deep(.el-date-picker__nav-btn) {
  color: #e2e8f0 !important;
}
</style>